import { pushErrorEventToDataLayer } from './pushErrorEventToDataLayer';
import { ErrorEventDetails, ErrorCategory, ErrorType } from './types';
import { APIError } from '../APIError';

interface IErrorTypeCategory {
  error_type: ErrorType;
  error_category: ErrorCategory;
}

export class AnalyticsSyntheticValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AnalyticsSyntheticValidationError';
    Object.setPrototypeOf(this, AnalyticsSyntheticValidationError.prototype);
  }
}

const isAnalyticsSyntheticValidationError = (error: any) => {
  return (
    error instanceof AnalyticsSyntheticValidationError ||
    error?.name === 'AnalyticsSyntheticValidationError'
  );
};

export const isAPIError = (error: any) => {
  return error instanceof APIError;
};

export const getAPIErrorStatusCode = (error: any) => {
  return isAPIError(error) ? error.getStatusCode() ?? '' : '';
};

export const getAPIErrorDescription = (error: any) => {
  return isAPIError(error) ? error.message : '';
};

// tslint:disable-next-line:cyclomatic-complexity
export function getErrorTypeAndCategory(error: any): IErrorTypeCategory {

  if (
    (typeof Response !== 'undefined' && error instanceof Response && !error.ok) ||
    isAPIError(error) ||
    error.message?.includes('Server error')) {
    return {
      error_type: 'api_failure',
      error_category: 'backend'
    };
  }
  if (error?.name === 'TypeError') {
    return {
      error_type: 'null_pointer_exception',
      error_category: 'frontend'
    };
  }
  if (isAnalyticsSyntheticValidationError(error)) {
    return {
      error_type: 'validation_error',
      error_category: 'frontend'
    };
  }
  return {
    error_type: 'unknown_error',
    error_category: 'unknown_error_category'
  };
}

export function trackErrorEvent(
  error: any,
  details?: Partial<
    Omit<ErrorEventDetails, 'error_category' | 'error_object'>
  >
): void {
  const { error_type, error_category } = getErrorTypeAndCategory(error);

  const errorEvent: ErrorEventDetails = {
    error_type: details?.error_type ?? error_type,
    error_category,
    error_code: details?.error_code ?? getAPIErrorStatusCode(error),
    error_description: isAPIError(error)
      ? [details?.error_description, getAPIErrorDescription(error)].filter(Boolean).join(' ')
      : details?.error_description ?? error?.message ?? error?.msg ?? '',
    error_location: details?.error_location ?? 'unknown',
    page_url: details?.page_url ?? window.location.pathname,
  };

  pushErrorEventToDataLayer(errorEvent);
}
